import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import styles from "./addsingle.module.scss";
import { handleGetRequest } from "../../../services/GetTemplate";

function AddSingleDialog({ onSubmit, editData }) {
    const [couriers, setCouriers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [billTo, setBillTo] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [initialValues, setInitialValues] = useState({
        description: "",
        quantity: 1,
        packageName: "",
        courierName: "",
        trackingNumber: "",
        courier: "",
        createdBy: "",
        creatorType: "",
        billTo: "",
        id: "",
        billToName: "",
        arrivalTime: "",
        merchant: "",
        deliveryInfo: "",
        consigneeType: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [couriersResult, usersResult] = await Promise.all([handleGetRequest("/userRole?role=courier"), handleGetRequest("/clientAndAdmins")]);

                setCouriers(couriersResult.data);

                const userOptions = usersResult.data.map((user) => ({
                    value: user._id,
                    label: `${user.firstName} ${user.lastName}`,
                    ...user,
                }));
                setAllUsers(userOptions);

                const storedUserId = localStorage.getItem("userId");
                const storedUserName = localStorage.getItem("user");
                const storedUserRole = localStorage.getItem("role")?.toLowerCase();

                if (editData) {
                    // Edit mode initialization
                    const matchedBillTo = userOptions.find((opt) => opt.value === editData.billTo);
                    setBillTo(matchedBillTo);

                    setInitialValues({
                        description: editData.description || "",
                        quantity: editData.quantity || 1,
                        packageName: editData.packageName || "",
                        courierName: editData.courierName || "",
                        trackingNumber: editData.trackingNumber || "",
                        courier: editData.courier || "",
                        createdBy: editData.createdBy || "",
                        creatorType: editData.creatorType || "",
                        billTo: editData.billTo || "",
                        id: editData._id || "",
                        billToName: matchedBillTo?.label || "",
                        arrivalTime: editData.arrivalTime || "",
                        merchant: editData.merchant || "",
                        deliveryInfo: editData.deliveryInfo || "",
                        consigneeType: editData.consigneeType || "",
                    });
                } else {
                    // Add mode initialization
                    const isClientType = ["client", "corporate", "reseller"].includes(storedUserRole);
                    const currentUser = userOptions.find((u) => u.value === storedUserId);

                    // Find current user in couriers list if they're a courier
                    const currentCourier = storedUserRole === "courier" ? couriersResult.data.find((c) => c._id === storedUserId) : null;

                    let courierId = storedUserId || "";
                    let courierName = storedUserName || "";
                    let deliveryInfo = currentUser?.deliveryInfo || "";

                    // If user is a courier, use their delivery info
                    if (currentCourier) {
                        deliveryInfo = currentCourier.deliveryInfo || deliveryInfo;
                    }

                    // If client/corporate/reseller, use their courierBase if available
                    if (isClientType && currentUser?.courierBase) {
                        courierId = currentUser.courierBase;
                        const foundCourier = couriersResult.data.find((c) => c._id === courierId);
                        if (foundCourier) {
                            courierName = foundCourier?.courier?.businessName || `${foundCourier?.name?.firstName} ${foundCourier?.name?.lastName}`;
                            deliveryInfo = foundCourier.deliveryInfo || deliveryInfo;
                        }
                    }

                    setInitialValues({
                        description: "",
                        quantity: 1,
                        packageName: "",
                        courierName,
                        trackingNumber: "",
                        courier: courierId,
                        createdBy: storedUserName || "",
                        creatorType: storedUserId ? "courier" : "admin",
                        billTo: storedUserId || "",
                        billToName: storedUserName || "",
                        id: "",
                        arrivalTime: "",
                        merchant: "",
                        deliveryInfo,
                        consigneeType: storedUserRole,
                    });
                }

                setIsAdmin(storedUserRole === "admin");
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [editData]);

    const validationSchema = Yup.object({
        // description: Yup.string().required("Description is required"),
        // quantity: Yup.number().min(1, "Quantity must be at least 1").required("Quantity is required"),
        // packageName: Yup.string().required("Package name is required"),
        // trackingNumber: Yup.string().required("Tracking number is required"),
        // ...(isAdmin && {
        //     courier: Yup.string().required("Courier is required"),
        //     billTo: Yup.string().required("Bill to is required")
        // })
    });

    const handleSubmit = (values, { resetForm }) => {
        if (!onSubmit) return;

        const submitValues = { ...values };

        if (isAdmin && !editData && billTo) {
            submitValues.billTo = billTo.value;
            submitValues.billToName = billTo.label;
        }

        onSubmit(submitValues, !!editData);
        resetForm();
    };

    const handleBillToChange = (selectedOption, setFieldValue) => {
        setFieldValue("billTo", selectedOption.value);
        setFieldValue("billToName", selectedOption.label);
        setFieldValue("deliveryInfo", selectedOption.deliveryInfo || "");
        setFieldValue("consigneeType", selectedOption.role || "client");
        setBillTo(selectedOption);

        console.log("Selected Bill To:", selectedOption);
    };

    const handleCourierChange = (e, setFieldValue) => {
        const selectedCourier = couriers.find((c) => c._id === e.target.value);
        const courierName = selectedCourier?.courier?.businessName || `${selectedCourier?.name?.firstName} ${selectedCourier?.name?.lastName}`;

        setFieldValue("courier", e.target.value);
        setFieldValue("courierName", courierName);
    };

    const handleTypeChange = (e, setFieldValue) => {
        setFieldValue("consigneeType", e.target.value);
    };

    return (
        <div className={styles.container}>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                {({ values, setFieldValue }) => (
                    <Form className={styles.form}>
                        <div className={styles.field}>
                            <label>Description</label>
                            <Field name="description" type="text" />
                            <ErrorMessage name="description" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Quantity</label>
                            <Field name="quantity" type="number" min="1" />
                            <ErrorMessage name="quantity" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Package Name</label>
                            <Field name="packageName" type="text" />
                            <ErrorMessage name="packageName" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Estimated arrival time</label>
                            <Field name="arrivalTime" type="date" />
                            <ErrorMessage name="arrivalTime" component="div" className={styles.error} />
                        </div>

                        <div className={styles.field}>
                            <label>Merchant</label>
                            <Field name="merchant" type="text" />
                            <ErrorMessage name="merchant" component="div" className={styles.error} />
                        </div>

                        {/* <div className={styles.field}>
                            <label>Delivery Info</label>
                            <Field name="deliveryInfo" as="textarea" />
                            <ErrorMessage name="deliveryInfo" component="div" className={styles.error} />
                        </div> */}

                        {isAdmin && (
                            <>
                                <div className={styles.field}>
                                    <label>Bill To</label>
                                    <Select name="billTo" options={allUsers} value={allUsers.find((option) => option.value === values.billTo) || null} onChange={(selectedOption) => handleBillToChange(selectedOption, setFieldValue)} placeholder="Select user" />
                                    <ErrorMessage name="billTo" component="div" className={styles.error} />
                                </div>
                                <div className={styles.field}>
                                    <label>Courier Name</label>
                                    <Field name="courier">
                                        {({ field }) => (
                                            <select {...field} onChange={(e) => handleCourierChange(e, setFieldValue)}>
                                                <option value="">Select Courier</option>
                                                {couriers.map((courier) => (
                                                    <option key={courier._id} value={courier._id}>
                                                        {courier?.courier?.businessName || `${courier?.name?.firstName} ${courier?.name?.lastName}`}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </Field>
                                    <ErrorMessage name="courier" component="div" className={styles.error} />
                                </div>
                                <div className={styles.field}>
                                    <label>Client Type</label>
                                    <Field name="consigneeType">
                                        {({ field }) => (
                                            <select {...field} onChange={(e) => handleTypeChange(e, setFieldValue)}>
                                                <option value="">Select Client type</option>
                                                <option value="client">Client</option>
                                                <option value="corporate">Corporate</option>
                                                <option value="reseller">Reseller</option>
                                                <option value="courier">Courier</option>
                                                <option value="admin">Admin</option>
                                            </select>
                                        )}
                                    </Field>
                                    <ErrorMessage name="consigneeType" component="div" className={styles.error} />
                                </div>
                            </>
                        )}

                        <div className={styles.field}>
                            <label>Tracking Number</label>
                            <Field name="trackingNumber" type="text" />
                            <ErrorMessage name="trackingNumber" component="div" className={styles.error} />
                        </div>

                        <button type="submit" className={styles.submitButton}>
                            {editData ? "Update Pending Package" : "Add Pending Package"}
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
}

export default AddSingleDialog;
